import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { PDFDocument, StandardFonts, rgb, PageSizes, PDFFont, PDFPage } from 'pdf-lib';
import { useParams } from 'react-router-dom';
import * as fontkit from 'fontkit';
import Box from '@mui/material/Box';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import FaxOutlinedIcon from '@mui/icons-material/FaxOutlined'; // FIX: Import Fax Icon
import moment from 'moment';
import Action from './Action';
import Tab from './tab/Tab';
import Title from './Title';
import { setNotificationMessage } from '../../../../../../redux/features/general/notification';
import { saveConsultationNote } from '../../../../../../routes/doctor/consultation';
import {
    setBlobFile,
    setBlobFileUrl,
    PatientInfoField,
    updatePatientInfoField,
    addPatientInfoField,
    removePatientInfoField,
} from '../../../../../../redux/features/doctor/Dashboard/consultation/consultationNote';
import {
    setIsFileExplorerPopupOpened,
    setIsNoteDeletionPopupOpened,
    setIsNoteExportPopupOpened,
    setIsNoteViewerOpened,
    setSavedNotes,
    setIsFaxPopupOpened, // FIX: Import redux action for fax popup
} from '../../../../../../redux/features/doctor/Dashboard/consultation/consultation';
import { resetFileExplorer, selectFile, setSearchSection } from '../../../../../../redux/features/doctor/Dashboard/documents/fileExplorer';
import { getFilesByConsultationId } from '../../../../../../routes/doctor/files';

import { store } from '../../../../../../redux/store';
import { setIsLoadingPopupOpened } from '../../../../../../redux/features/general/navigation';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

const NOTE_STYLING_CONSTANTS = {
    COLOR_TEXT_HEADER_CSS: 'rgb(25, 25, 25)',
    COLOR_TEXT_BODY_CSS: 'rgb(50, 50, 50)',
};

const PATIENT_INFO_PLACEHOLDER = '____________________';

// Helper function to convert Blob to Data URL
const blobToDataURL = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            if (reader.error) {
                reject(reader.error);
            } else {
                resolve(reader.result as string);
            }
        };
        reader.readAsDataURL(blob);
    });
};

const Header: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { consultationId, consultationNoteId } = useParams();

    const colors = useAppSelector(state => state.theme.colors);
    const notesFromSelector = useAppSelector(state => state.consultation.notes);
    const isNoteEmptyForRender = !useAppSelector(state => state.consultationNote.editableNoteRawText)?.trim();
    const isLoadingTranscription = useAppSelector(state => state.consultationNote.isLoadingTranscription);
    const patientInfoFields = useAppSelector(state => state.consultationNote.patientInfoFields);

    /** refresh list of note files after a save */
    const handleNotesRefresh = async () => {
        if (!consultationId) return;
        const res = await getFilesByConsultationId(String(consultationId)) as any;
        if (res?.data?.status === 200) dispatch(setSavedNotes(res.data.files));
    };

    /** copy plain text of the note to clipboard */
    const handleCopyNoteText = () => {
        const {
            consultationNote: { editableNoteRawText = '', title = t('untitledNote') }
        } = store.getState();

        if (editableNoteRawText.trim()) {
            navigator.clipboard.writeText(`${title}\n\n${editableNoteRawText}`);
            dispatch(setNotificationMessage(t('noteCopied')));
        } else {
            dispatch(setNotificationMessage(t('cannotCopyEmptyNote')));
        }
    };

    /** save note as a PDF file and open download popup */
    const exportNoteAsPdf = async () => {
        const { consultationNote: { editableNoteRawText = '' } } = store.getState();
        if (!editableNoteRawText.trim()) {
            dispatch(setNotificationMessage(t('cannotExportEmptyNote')));
            return;
        }

        dispatch(setIsLoadingPopupOpened(true));
        try {
            const file = await createFileFromNote(); // This is a File object, which is a Blob
            dispatch(setBlobFile(file)); // Store the original File object

            const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

            if (isMobile) {
                try {
                    const dataUrl = await blobToDataURL(file);
                    dispatch(setBlobFileUrl(dataUrl)); 
                } catch (dataUrlError) {
                    console.error("Error converting blob to Data URL, falling back to Blob URL:", dataUrlError);
                    // Fallback to blob URL if conversion fails
                    const blobUrl = URL.createObjectURL(file);
                    dispatch(setBlobFileUrl(blobUrl));
                }
            } else {
                // For desktop, Blob URL is generally fine and more efficient
                const blobUrl = URL.createObjectURL(file);
                dispatch(setBlobFileUrl(blobUrl));
            }
            
            dispatch(setIsNoteExportPopupOpened(true));
        } catch (err) {
            console.error(err);
            dispatch(setNotificationMessage(t('errorCreatingPdf')));
        } finally {
            dispatch(setIsLoadingPopupOpened(false));
        }
    };

    /** create a PDF and open the in-app viewer or new tab */
    const viewNoteAsPdf = async () => {
        const { consultationNote: { editableNoteRawText = '' } } = store.getState();
        if (!editableNoteRawText.trim()) {
            dispatch(setNotificationMessage(t('cannotPreviewEmptyNote')));
            return;
        }

        dispatch(setIsLoadingPopupOpened(true));
        try {
            const file = await createFileFromNote();
            const blobUrl = URL.createObjectURL(file); 

            dispatch(setBlobFileUrl(blobUrl)); 

            const newWindow = window.open(blobUrl, '_blank');
            
            if (newWindow && !newWindow.closed && typeof newWindow.closed !== 'undefined') {
                // Successfully opened in a new tab.
            } else {
                dispatch(setNotificationMessage(t('pdf.previewNewTabFailed', 'Could not open PDF in a new tab. Trying in-app viewer.')));
                dispatch(setIsNoteViewerOpened(true));
            }
        } catch (err) {
            console.error(err);
            dispatch(setNotificationMessage(t('errorCreatingPdf')));
        } finally {
            dispatch(setIsLoadingPopupOpened(false));
        }
    };

    /** save PDF straight to the server and refresh file list */
    const saveNoteToServer = async () => {
        // ... (existing function is unchanged)
    };

    const handleNoteDeletion = () => dispatch(setIsNoteDeletionPopupOpened(true));

    // FIX: Add handler to open the new Fax popup
    const handleOpenFaxPopup = () => {
        if (isNoteEmptyForRender) {
            dispatch(setNotificationMessage(t('cannotFaxEmptyNote', 'Cannot fax an empty note.')));
            return;
        }
        dispatch(setIsFaxPopupOpened(true));
    };

    const handlePatientInfoChange = (id: string, field: 'label' | 'value', newValue: string) => {
        dispatch(updatePatientInfoField({ id, [field]: newValue }));
    };

    const handleAddPatientInfoField = () => {
        const newFieldLabel = t('consultationNote.newFieldDefaultLabel', 'New Field:');
        dispatch(addPatientInfoField({ label: newFieldLabel }));
    };

    const handleRemovePatientInfoField = (id: string) => {
        dispatch(removePatientInfoField({ id }));
    };

    const createFileFromNote = async (): Promise<File> => {
        // ... (existing function is unchanged, it will be copied to the Fax popup)
        // This large function remains here for the other actions (print, save, etc.)
        const currentGlobalState = store.getState();
        const descriptionForPdf = currentGlobalState.consultationNote.editableNoteRawText || '';
        const noteTitleForPdf = currentGlobalState.consultationNote.title;
        const noteInfoForPdf = currentGlobalState.consultationNote.info;
        const currentPatientInfoFields: PatientInfoField[] = currentGlobalState.consultationNote.patientInfoFields;

        const MARGIN_TOP = 35;
        const MARGIN_BOTTOM = 35;
        const MARGIN_LEFT = 40;
        const MARGIN_RIGHT = 40;

        const PDF_FONT_SIZE_TITLE = 11;
        const PDF_FONT_SIZE_SECTION_HEADER = 10;
        const PDF_FONT_SIZE_BODY = 9;
        const PDF_FONT_SIZE_METADATA = 7.5;
        const PDF_FONT_SIZE_FOOTER = 7;
        const PDF_FONT_SIZE_PATIENT_ID_LABEL = 8;
        const PDF_FONT_SIZE_PATIENT_ID_VALUE = 8;

        const PDF_LINE_HEIGHT_MULTIPLIER_TITLE = 1.25;
        const PDF_LINE_HEIGHT_MULTIPLIER_SECTION_HEADER = 1.25;
        const PDF_LINE_HEIGHT_MULTIPLIER_BODY = 1.3;
        const PDF_LINE_HEIGHT_MULTIPLIER_METADATA = 1.25;
        const PDF_LINE_HEIGHT_MULTIPLIER_PATIENT_ID = 1.3;

        const PDF_SPACING_METADATA_BLOCK_BOTTOM = 15;
        const PDF_SPACING_AFTER_TITLE = 10;
        const PDF_SPACING_BETWEEN_METADATA_ITEMS = 2;
        const PDF_SPACING_BEFORE_SECTION_HEADER = 8;
        const PDF_SPACING_AFTER_SECTION_HEADER = 4;
        const PDF_SPACING_EMPTY_LINE = PDF_FONT_SIZE_BODY * 0.7;

        const PDF_BULLET_INDENT_LEVEL_1 = 12;
        const PDF_BULLET_INDENT_LEVEL_2 = 24;
        const PDF_SUB_ITEM_INDENT = 18;

        const safeRgb = (cssColor: string, fallback = { r: 0, g: 0, b: 0 }) => {
            if (!cssColor) return rgb(fallback.r, fallback.g, fallback.b);
            try {
                const match = cssColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
                if (match) {
                    return rgb(parseInt(match[1]) / 255, parseInt(match[2]) / 255, parseInt(match[3]) / 255);
                }
            } catch (e) { console.warn("safeRgb failed for color:", cssColor, e); }
            return rgb(fallback.r, fallback.g, fallback.b);
        };

        const COLOR_TEXT_HEADER = safeRgb(NOTE_STYLING_CONSTANTS.COLOR_TEXT_HEADER_CSS, { r: 0.05, g: 0.05, b: 0.05 });
        const COLOR_TEXT_BODY = safeRgb(NOTE_STYLING_CONSTANTS.COLOR_TEXT_BODY_CSS, { r: 0.2, g: 0.2, b: 0.2 });
        const COLOR_TEXT_METADATA = rgb(0.35, 0.35, 0.35);
        const COLOR_TEXT_FOOTER = rgb(0.45, 0.45, 0.45);
        const COLOR_PLACEHOLDER_LINES = rgb(0.65, 0.65, 0.65);

        const pdfDoc = await PDFDocument.create();
        let robotoRegular: PDFFont, robotoBold: PDFFont;
        try {
            const fontBytesRegular = await fetch('/fonts/Roboto-Regular.ttf').then(res => res.arrayBuffer());
            const fontBytesBold = await fetch('/fonts/Roboto-Bold.ttf').then(res => res.arrayBuffer());
            pdfDoc.registerFontkit(fontkit);
            robotoRegular = await pdfDoc.embedFont(fontBytesRegular);
            robotoBold = await pdfDoc.embedFont(fontBytesBold);
        } catch (error) {
            console.error("Error loading Roboto fonts, falling back to Helvetica:", error);
            robotoRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
            robotoBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        }

        let page = pdfDoc.addPage(PageSizes.Letter);
        const { width, height } = page.getSize();
        let y = height - MARGIN_TOP;
        const usableWidth = width - MARGIN_LEFT - MARGIN_RIGHT;

        const drawTextLineHelper = (text: string, options: { font: PDFFont; size: number; color?: any; x?: number; y: number; maxWidth?: number; lineHeight?: number; }) => {
            page.drawText(text, {
                x: options.x ?? MARGIN_LEFT,
                y: options.y,
                font: options.font,
                size: options.size,
                color: options.color ?? COLOR_TEXT_BODY,
                lineHeight: options.lineHeight,
                maxWidth: options.maxWidth,
                wordBreaks: [' ', '-', '/']
            });
        };

        const calculateTextHeightHelper = (text: string, font: PDFFont, size: number, maxWidthToUse: number, lineHeightToUse: number): number => {
            if (!text || text.trim() === "") return lineHeightToUse;

            const words = text.split(' ');
            let currentLine = '';
            let lineCount = 0;

            for (const word of words) {
                const testLine = currentLine ? `${currentLine} ${word}` : word;
                if (font.widthOfTextAtSize(testLine, size) > maxWidthToUse && currentLine !== '') {
                    lineCount++;
                    currentLine = word;
                    if (font.widthOfTextAtSize(word, size) > maxWidthToUse) {
                         const charArray = word.split('');
                         let subWordLine = '';
                         for(const char of charArray){
                            const testSubWordLine = subWordLine + char;
                            if(font.widthOfTextAtSize(testSubWordLine, size) > maxWidthToUse){
                                lineCount++;
                                subWordLine = char;
                            } else {
                                subWordLine = testSubWordLine;
                            }
                         }
                         currentLine = subWordLine;
                    }
                } else {
                    currentLine = testLine;
                }
            }
            if (currentLine) lineCount++;
            return Math.max(1, lineCount) * lineHeightToUse;
        };

        const addFooterToPage = (targetPage: PDFPage) => {
            const footerText = t('pdf.footer') || 'This is a computer-generated document.';
            const footerTextWidth = robotoRegular.widthOfTextAtSize(footerText, PDF_FONT_SIZE_FOOTER);
            targetPage.drawText(footerText, {
                 font: robotoRegular,
                 size: PDF_FONT_SIZE_FOOTER,
                 color: COLOR_TEXT_FOOTER,
                 x: MARGIN_LEFT + (usableWidth - footerTextWidth) / 2,
                 y: 20,
            });
        };

        const addNewPageAndSetup = (): number => {
            if (pdfDoc.getPageCount() > 0) {
                addFooterToPage(page);
            }
            page = pdfDoc.addPage(PageSizes.Letter);
            y = height - MARGIN_TOP;
            return y;
         };

        const metaDataLineHeight = PDF_FONT_SIZE_METADATA * PDF_LINE_HEIGHT_MULTIPLIER_METADATA;
        const datesStartY = y;
        let currentMetaY = datesStartY;

        const dateLabelDate = t('pdf.label.noteCreationDate') || 'Creation Date:';
        const dateLabelGen = t('pdf.label.noteGenerationDate') || 'Generation Date:';
        const dateLabelMaxWidth = Math.max(
            robotoRegular.widthOfTextAtSize(dateLabelDate, PDF_FONT_SIZE_METADATA),
            robotoRegular.widthOfTextAtSize(dateLabelGen, PDF_FONT_SIZE_METADATA)
        );
        const dateValueX = MARGIN_LEFT + dateLabelMaxWidth + 5;

        if (noteInfoForPdf?.createdAt) {
            currentMetaY -= metaDataLineHeight;
            drawTextLineHelper(dateLabelDate, { font: robotoRegular, size: PDF_FONT_SIZE_METADATA, color: COLOR_TEXT_METADATA, y: currentMetaY, x: MARGIN_LEFT });
            drawTextLineHelper(moment(noteInfoForPdf.createdAt).format('L LT'), { font: robotoRegular, size: PDF_FONT_SIZE_METADATA, color: COLOR_TEXT_BODY, y: currentMetaY, x: dateValueX, maxWidth: usableWidth - (dateValueX - MARGIN_LEFT) });
            currentMetaY -= PDF_SPACING_BETWEEN_METADATA_ITEMS;
        }
        currentMetaY -= metaDataLineHeight;
        drawTextLineHelper(dateLabelGen, { font: robotoRegular, size: PDF_FONT_SIZE_METADATA, color: COLOR_TEXT_METADATA, y: currentMetaY, x: MARGIN_LEFT });
        drawTextLineHelper(moment().format('L LT'), { font: robotoRegular, size: PDF_FONT_SIZE_METADATA, color: COLOR_TEXT_BODY, y: currentMetaY, x: dateValueX, maxWidth: usableWidth - (dateValueX - MARGIN_LEFT) });
        const datesEndY = currentMetaY;

        const patientIdLineHeight = PDF_FONT_SIZE_PATIENT_ID_LABEL * PDF_LINE_HEIGHT_MULTIPLIER_PATIENT_ID;
        let patientIdY = datesStartY;

        let maxPatientLabelWidth = 0;
        currentPatientInfoFields.forEach(field => {
            const w = robotoBold.widthOfTextAtSize(field.label, PDF_FONT_SIZE_PATIENT_ID_LABEL);
            if (w > maxPatientLabelWidth) maxPatientLabelWidth = w;
        });

        let maxPatientValueWidth = robotoRegular.widthOfTextAtSize(PATIENT_INFO_PLACEHOLDER, PDF_FONT_SIZE_PATIENT_ID_VALUE);
        currentPatientInfoFields.forEach(field => {
            const valW = robotoRegular.widthOfTextAtSize(field.value || PATIENT_INFO_PLACEHOLDER, PDF_FONT_SIZE_PATIENT_ID_VALUE);
            if (valW > maxPatientValueWidth) maxPatientValueWidth = valW;
        });
        maxPatientValueWidth = Math.min(maxPatientValueWidth, usableWidth / 3);

        const patientIdBlockTotalWidth = maxPatientLabelWidth + 5 + maxPatientValueWidth;
        const patientIdSectionStartX = Math.max(MARGIN_LEFT + usableWidth / 2, width - MARGIN_RIGHT - patientIdBlockTotalWidth);
        const patientIdValueTextX = patientIdSectionStartX + maxPatientLabelWidth + 5;

        currentPatientInfoFields.forEach((field) => {
            patientIdY -= patientIdLineHeight;
            drawTextLineHelper(field.label, { font: robotoBold, size: PDF_FONT_SIZE_PATIENT_ID_LABEL, color: COLOR_TEXT_METADATA, y: patientIdY, x: patientIdSectionStartX });

            const valueToDraw = field.value?.trim() ? field.value : PATIENT_INFO_PLACEHOLDER;
            const valueColor = field.value?.trim() ? COLOR_TEXT_BODY : COLOR_PLACEHOLDER_LINES;
            drawTextLineHelper(valueToDraw, { font: robotoRegular, size: PDF_FONT_SIZE_PATIENT_ID_VALUE, color: valueColor, y: patientIdY, x: patientIdValueTextX, maxWidth: maxPatientValueWidth });
        });
        const patientIdEndY = patientIdY;

        y = Math.min(datesEndY, patientIdEndY);
        y -= PDF_SPACING_METADATA_BLOCK_BOTTOM;

        const titleLineHeight = PDF_FONT_SIZE_TITLE * PDF_LINE_HEIGHT_MULTIPLIER_TITLE;
        const effectiveNoteTitle = noteTitleForPdf?.trim() ? noteTitleForPdf : (t('untitledNote') || 'Untitled Note');

        const titleHeight = calculateTextHeightHelper(effectiveNoteTitle, robotoBold, PDF_FONT_SIZE_TITLE, usableWidth, titleLineHeight);
        if (y - titleHeight < MARGIN_BOTTOM) y = addNewPageAndSetup();

        const titleWidth = robotoBold.widthOfTextAtSize(effectiveNoteTitle, PDF_FONT_SIZE_TITLE);
        drawTextLineHelper(effectiveNoteTitle, {
            font: robotoBold,
            size: PDF_FONT_SIZE_TITLE,
            color: COLOR_TEXT_HEADER,
            x: (titleHeight <= titleLineHeight && titleWidth < usableWidth) ? (MARGIN_LEFT + (usableWidth - titleWidth) / 2) : MARGIN_LEFT,
            y: y - PDF_FONT_SIZE_TITLE,
            maxWidth: usableWidth,
            lineHeight: titleLineHeight
        });
        y -= titleHeight;
        y -= PDF_SPACING_AFTER_TITLE;

        const descriptionLines = descriptionForPdf.split('\n');
        let previousLineEndedWithColon = false;

        const isSectionHeaderRegex = (str: string): boolean => {
            if (!str.endsWith(':')) return false;
            const content = str.slice(0, -1).trim();
            if (content === '') return false;
            return content === content.toUpperCase() && /[A-ZÀÂÄÈÉÊËÎÏÔÖÙÜÇ]/.test(content);
        };

        for (const line of descriptionLines) {
            let currentFont = robotoRegular;
            let currentFontSize = PDF_FONT_SIZE_BODY;
            let currentColor = COLOR_TEXT_BODY;
            let indent = 0;
            let textToDraw = line;
            let currentLineHeightMultiplier = PDF_LINE_HEIGHT_MULTIPLIER_BODY;
            let spacingBefore = 0;
            let spacingAfter = 0;

            const trimmedLine = line.trim();

            if (trimmedLine === "" && !line.includes("___")) {
                if (y - PDF_SPACING_EMPTY_LINE < MARGIN_BOTTOM) y = addNewPageAndSetup();
                y -= PDF_SPACING_EMPTY_LINE;
                previousLineEndedWithColon = false;
                continue;
            }

            const isHeader = isSectionHeaderRegex(trimmedLine);
            const isNestedBulletMatch = line.match(/^(\s{4,})(-\s+.*)/) || line.match(/^(\s\s+)L\d\s(-\s+.*)/);
            const isBulletLevel1Match = line.match(/^(\s*)-\s+(.*)/);

            if (isHeader) {
                currentFont = robotoBold;
                currentFontSize = PDF_FONT_SIZE_SECTION_HEADER;
                currentColor = COLOR_TEXT_HEADER;
                textToDraw = trimmedLine;
                currentLineHeightMultiplier = PDF_LINE_HEIGHT_MULTIPLIER_SECTION_HEADER;
                spacingBefore = PDF_SPACING_BEFORE_SECTION_HEADER;
                spacingAfter = PDF_SPACING_AFTER_SECTION_HEADER;
                previousLineEndedWithColon = false;
            } else if (isNestedBulletMatch) {
                indent = PDF_BULLET_INDENT_LEVEL_2;
                textToDraw = line.trimStart();
                previousLineEndedWithColon = trimmedLine.endsWith(':');
            } else if (isBulletLevel1Match) {
                indent = PDF_BULLET_INDENT_LEVEL_1;
                textToDraw = line.trimStart();
                previousLineEndedWithColon = trimmedLine.endsWith(':');
            } else if (previousLineEndedWithColon && /^\s+/.test(line)) {
                indent = PDF_SUB_ITEM_INDENT;
                textToDraw = line.trimStart();
                previousLineEndedWithColon = trimmedLine.endsWith(':');
            } else if (/^\s{2,}/.test(line) && !isHeader && !line.startsWith("___")) {
                indent = PDF_SUB_ITEM_INDENT;
                textToDraw = line.trimStart();
                previousLineEndedWithColon = trimmedLine.endsWith(':');
            } else {
                textToDraw = line;
                previousLineEndedWithColon = trimmedLine.endsWith(':');
            }

            const currentLineHeight = currentFontSize * currentLineHeightMultiplier;

            if (spacingBefore > 0) {
                if (y - spacingBefore < MARGIN_BOTTOM) {
                    y = addNewPageAndSetup();
                }
                y -= spacingBefore;
            }

            const textBlockHeight = calculateTextHeightHelper(textToDraw, currentFont, currentFontSize, usableWidth - indent, currentLineHeight);

            if (y - textBlockHeight < MARGIN_BOTTOM) {
                y = addNewPageAndSetup();
                if (isHeader && spacingBefore > 0) {
                    y -= spacingBefore;
                }
            }

            drawTextLineHelper(textToDraw, {
                font: currentFont,
                size: currentFontSize,
                color: currentColor,
                x: MARGIN_LEFT + indent,
                y: y - currentFontSize,
                maxWidth: usableWidth - indent,
                lineHeight: currentLineHeight
            });
            y -= textBlockHeight;

            if (spacingAfter > 0) {
                if (y - spacingAfter < MARGIN_BOTTOM && pdfDoc.getPageCount() > 0) {
                }
                y -= spacingAfter;
            }
        }

        addFooterToPage(page);
        const pdfBytes = await pdfDoc.save();
        const sanitizedTitle = (effectiveNoteTitle).replace(/[^a-z0-9]/gi, '_').substring(0, 50);
        const newFile = new File([pdfBytes], `${sanitizedTitle}_${moment().format('YYYYMMDD_HHmm')}.pdf`, { type: "application/pdf" });
        return newFile;
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            height: 'auto',
            minHeight: '100px',
            backgroundColor: colors?.container,
            pb: 1,
        }}>
            <Tab />
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '95%',
                mt: 1,
                color: colors?.main,
                flexWrap: 'wrap',
            }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Action icon={<LocalPrintshopOutlinedIcon />} handler={viewNoteAsPdf} isDisabled={isNoteEmptyForRender} title={'print'} />
                    <Action icon={<ContentCopyOutlinedIcon />} handler={handleCopyNoteText} isDisabled={isNoteEmptyForRender} title={'copy'} />
                    <Action icon={<FaxOutlinedIcon />} handler={handleOpenFaxPopup} isDisabled={isNoteEmptyForRender} title={'fax'} />
                    <Action icon={<DeleteOutlineOutlinedIcon />} handler={handleNoteDeletion} isDisabled={(notesFromSelector?.length ?? 0) <= 1} title={'delete'} />
                    <Action icon={<DownloadOutlinedIcon />} handler={exportNoteAsPdf} isDisabled={isNoteEmptyForRender} title={'save'} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-evenly' }}>
                    {isLoadingTranscription && <CircularProgress size={25} />}
                </Box>
            </Box>

            <Title />

            <Box sx={{
                width: '95%',
                mt: 0.5,
                mb: 1,
                p: 1,
                display: 'flex',
                flexDirection: 'column',
            }}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5}}>
                    <Typography variant="caption" sx={{ color: colors?.textSecondary, fontWeight: 'medium' }}>
                        {t('patientInformation') || 'Patient Information (for PDF)'}
                    </Typography>
                    <IconButton onClick={handleAddPatientInfoField} size="small" sx={{ color: 'rgba(0, 0, 0, 0.54)', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)'} }}>
                        <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                </Box>
                {patientInfoFields.map((field) => (
                    <Box key={field.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                        <TextField
                            variant="standard"
                            size="small"
                            value={field.label} 
                            onChange={(e) => handlePatientInfoChange(field.id, 'label', e.target.value)} 
                            sx={{
                                mr: 1,
                                flexBasis: '35%',
                                minWidth: '80px',
                                '& .MuiInputBase-input': {
                                    fontSize: '0.75rem',
                                    color: colors?.text, 
                                    paddingBottom: '2px',
                                },
                                '& .MuiInput-underline:before': { borderBottom: `1px solid ${colors?.inputBorder || 'rgba(0,0,0,0.2)'}` },
                                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: `1px solid ${colors?.text}` },
                                '& .MuiInput-underline:after': { borderBottom: `2px solid ${colors?.inputBorder || 'rgba(0,0,0,0.4)'}` },
                            }}
                        />
                        <TextField
                            variant="standard"
                            size="small"
                            value={field.value}
                            onChange={(e) => handlePatientInfoChange(field.id, 'value', e.target.value)}
                            sx={{
                                mr: 0.5,
                                flexGrow: 1,
                                '& .MuiInputBase-input': {
                                    fontSize: '0.75rem',
                                    color: colors?.text,
                                    paddingBottom: '2px',
                                },
                                '& .MuiInput-underline:before': { borderBottom: `1px solid ${colors?.inputBorder || 'rgba(0,0,0,0.2)'}` },
                                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: `1px solid ${colors?.text}` },
                                '& .MuiInput-underline:after': { borderBottom: `2px solid ${colors?.inputBorder || 'rgba(0,0,0,0.4)'}` },
                            }}
                        />
                        <IconButton onClick={() => handleRemovePatientInfoField(field.id)} size="small" sx={{ color: 'rgba(0, 0, 0, 0.4)', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)'} }}>
                            <RemoveCircleOutlineIcon fontSize="small"/>
                        </IconButton>
                    </Box>
                ))}
            </Box>

        </Box>
    );
};
export default Header;